import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { MultiSelect } from '@/components/ui/multi-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Filter, RotateCcw, AlertCircle } from 'lucide-react';
import { validateFilters } from '@/utils/bulk-analysis-filters';
import { createDebouncedFilter } from '@/utils/bulk-analysis-filters';

/**
 * Advanced filtering interface for race data
 * Provides multi-select capabilities and real-time filter application
 */
const FilterPanel = ({
  onFiltersChange,
  availableOptions = {},
  currentFilters = {},
  className = "",
  debounceDelay = 300
}) => {
  const [localFilters, setLocalFilters] = useState({
    dateRange: { start: null, end: null },
    daysOfWeek: [],
    states: [],
    tracks: [],
    combinedOddsThreshold: null,
    horseCountRange: { min: null, max: null },
    raceTypes: [],
    ...currentFilters
  });

  const [filterErrors, setFilterErrors] = useState([]);

  // Memoized options for performance
  const stateOptions = useMemo(() => {
    return availableOptions.states || ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
  }, [availableOptions.states]);

  const trackOptions = useMemo(() => {
    return availableOptions.tracks || [];
  }, [availableOptions.tracks]);

  const daysOfWeekOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Debounced filter change handler
  const debouncedFilterChange = useMemo(
    () => createDebouncedFilter((filters) => {
      // Validate filters before applying
      const validation = validateFilters(filters);
      setFilterErrors(validation.errors);

      if (validation.isValid && onFiltersChange) {
        onFiltersChange(filters);
      }
    }, debounceDelay),
    [onFiltersChange, debounceDelay]
  );

  const handleFilterChange = useCallback((filterType, value) => {
    const updatedFilters = {
      ...localFilters,
      [filterType]: value
    };
    setLocalFilters(updatedFilters);
    debouncedFilterChange(updatedFilters);
  }, [localFilters, debouncedFilterChange]);

  // Update local filters when currentFilters prop changes
  useEffect(() => {
    setLocalFilters(prevFilters => ({
      ...prevFilters,
      ...currentFilters
    }));
  }, [currentFilters]);

  const handleDateRangeChange = useCallback((type, value) => {
    const updatedDateRange = {
      ...localFilters.dateRange,
      [type]: value
    };
    handleFilterChange('dateRange', updatedDateRange);
  }, [localFilters.dateRange, handleFilterChange]);

  const handleHorseCountChange = useCallback((type, value) => {
    const updatedRange = {
      ...localFilters.horseCountRange,
      [type]: value ? parseInt(value) : null
    };
    handleFilterChange('horseCountRange', updatedRange);
  }, [localFilters.horseCountRange, handleFilterChange]);

  const handleOddsThresholdChange = useCallback((value) => {
    handleFilterChange('combinedOddsThreshold', value[0]);
  }, [handleFilterChange]);

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {
      dateRange: { start: null, end: null },
      daysOfWeek: [],
      states: [],
      tracks: [],
      combinedOddsThreshold: null,
      horseCountRange: { min: null, max: null },
      raceTypes: []
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  }, [onFiltersChange]);

  // Calculate active filter count for display
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (localFilters.dateRange?.start || localFilters.dateRange?.end) count++;
    if (localFilters.daysOfWeek?.length > 0) count++;
    if (localFilters.states?.length > 0) count++;
    if (localFilters.tracks?.length > 0) count++;
    if (localFilters.combinedOddsThreshold !== null) count++;
    if (localFilters.horseCountRange?.min !== null || localFilters.horseCountRange?.max !== null) count++;
    return count;
  }, [localFilters]);

  return (
    <Card className={`filter-panel ${className}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeFilterCount} active
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-8 px-2"
            disabled={activeFilterCount === 0}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Date Range Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Date Range</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input
                type="date"
                value={localFilters.dateRange?.start || ''}
                onChange={(e) => handleDateRangeChange('start', e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input
                type="date"
                value={localFilters.dateRange?.end || ''}
                onChange={(e) => handleDateRangeChange('end', e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Days of Week Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Days of Week</Label>
          <MultiSelect
            value={localFilters.daysOfWeek || []}
            onChange={(value) => handleFilterChange('daysOfWeek', value)}
            options={daysOfWeekOptions}
            placeholder="Select days..."
            className="w-full"
          />
        </div>

        <Separator />

        {/* States Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">States</Label>
          <MultiSelect
            value={localFilters.states || []}
            onChange={(value) => handleFilterChange('states', value)}
            options={stateOptions}
            placeholder="Select states..."
            className="w-full"
          />
        </div>

        <Separator />

        {/* Tracks Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Tracks</Label>
          <MultiSelect
            value={localFilters.tracks || []}
            onChange={(value) => handleFilterChange('tracks', value)}
            options={trackOptions}
            placeholder="Select tracks..."
            className="w-full"
          />
          {trackOptions.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Track options will be loaded from available data
            </p>
          )}
        </div>

        <Separator />

        {/* Combined Odds Threshold */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Combined Odds Threshold</Label>
            {localFilters.combinedOddsThreshold && (
              <Badge variant="outline" className="text-xs">
                ≤ {localFilters.combinedOddsThreshold}
              </Badge>
            )}
          </div>
          <div className="space-y-3">
            <Slider
              value={[localFilters.combinedOddsThreshold || 50]}
              onValueChange={handleOddsThresholdChange}
              max={100}
              min={1}
              step={0.5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1.0</span>
              <span>100.0</span>
            </div>
          </div>
          <Input
            type="number"
            step="0.1"
            placeholder="Or enter exact value..."
            value={localFilters.combinedOddsThreshold || ''}
            onChange={(e) => handleFilterChange('combinedOddsThreshold',
              e.target.value ? parseFloat(e.target.value) : null)}
            className="text-sm"
          />
        </div>

        <Separator />

        {/* Horse Count Range */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Horse Count Range</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Min Horses</Label>
              <Input
                type="number"
                placeholder="e.g., 8"
                min="1"
                max="30"
                value={localFilters.horseCountRange?.min || ''}
                onChange={(e) => handleHorseCountChange('min', e.target.value)}
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Max Horses</Label>
              <Input
                type="number"
                placeholder="e.g., 16"
                min="1"
                max="30"
                value={localFilters.horseCountRange?.max || ''}
                onChange={(e) => handleHorseCountChange('max', e.target.value)}
                className="text-sm"
              />
            </div>
          </div>
          {(localFilters.horseCountRange?.min || localFilters.horseCountRange?.max) && (
            <div className="text-xs text-muted-foreground">
              Showing races with {localFilters.horseCountRange?.min || 'any'} to {localFilters.horseCountRange?.max || 'any'} horses
            </div>
          )}
        </div>

        <Separator />

        {/* Filter Validation Errors */}
        {filterErrors.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <Label className="text-sm font-medium">Filter Errors</Label>
            </div>
            <div className="space-y-1">
              {filterErrors.map((error, index) => (
                <div key={index} className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {activeFilterCount > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Active Filters</Label>
            <div className="flex flex-wrap gap-1">
              {localFilters.dateRange?.start && (
                <Badge variant="secondary" className="text-xs">
                  From: {localFilters.dateRange.start}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleDateRangeChange('start', null)}
                  />
                </Badge>
              )}
              {localFilters.dateRange?.end && (
                <Badge variant="secondary" className="text-xs">
                  To: {localFilters.dateRange.end}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleDateRangeChange('end', null)}
                  />
                </Badge>
              )}
              {localFilters.daysOfWeek?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {localFilters.daysOfWeek.length} day{localFilters.daysOfWeek.length > 1 ? 's' : ''}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleFilterChange('daysOfWeek', [])}
                  />
                </Badge>
              )}
              {localFilters.states?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {localFilters.states.length} state{localFilters.states.length > 1 ? 's' : ''}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleFilterChange('states', [])}
                  />
                </Badge>
              )}
              {localFilters.tracks?.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {localFilters.tracks.length} track{localFilters.tracks.length > 1 ? 's' : ''}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleFilterChange('tracks', [])}
                  />
                </Badge>
              )}
              {localFilters.combinedOddsThreshold && (
                <Badge variant="secondary" className="text-xs">
                  Odds ≤ {localFilters.combinedOddsThreshold}
                  <X
                    className="h-3 w-3 ml-1 cursor-pointer"
                    onClick={() => handleFilterChange('combinedOddsThreshold', null)}
                  />
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterPanel;